/**
 * 指定 timestamp 範囲の Action を高速取得する最適化クエリ。
 * 1) timestamp インデックスで action を LIMIT 付き取得
 * 2) 取得 actionId 群に対し transfers (+ crypto/nft) を一括取得
 * 3) アプリ層でグルーピング & パース
 */
import type {
  ActionCrossType,
  ActionType,
  FiatCurrency,
  TransferDirection,
} from '@0xtorch/core'
import {
  actionCrossTypeUnionSchema,
  actionTypeUnionSchema,
} from '@0xtorch/core'
import { type Action, divideArrayIntoChunks } from '@pkg/basic'
import { inArray, sql } from 'drizzle-orm'
import { z } from 'zod'
import { sqliteMaxHostParameterCount } from '../../constants'
import { parseToAction } from '../../parsers/action'
import type { ActionTransferData } from '../../parsers/actionTransfer'
import {
  actionTable,
  actionTransferTable,
  cryptoCurrencyTable,
  nftTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'

type ActionQueryRow = {
  readonly actionId: string
  readonly actionSourceId: string
  readonly actionOrder: number
  readonly actionType: ActionType
  readonly actionSubType: number | null
  readonly actionTimestamp: number
  readonly actionLoanId: string | null
  readonly actionCrossId: string | null
  readonly actionCrossType: ActionCrossType | null
  readonly actionTargetId: string | null
  readonly transferOrder: number | null
  readonly transferDirection: TransferDirection | null
  readonly transferCryptoId: string | null
  readonly transferFiatId: string | null
  readonly transferNftId: string | null
  readonly transferAmount: string | null
  readonly transferPrice: string | null
}

type RawQueryRowObject = Record<string, unknown>

/**
 * Transfer の方向として許可する文字列を検証する schema。
 */
const transferDirectionUnionSchema = z.enum(['in', 'out', 'none'])

type RawCryptoCurrencyRow = {
  readonly id: string
  readonly name: string
  readonly symbol: string
  readonly icon: string | null
  readonly coingeckoId: string | null
  readonly marketCapUsd: number | null
  readonly priceDatasourceId: string | null
  readonly updatedAt: number
}

type RawNftRow = {
  readonly id: string
  readonly name: string | null
  readonly image: string | null
  readonly metadata: string | null
  readonly updatedAt: number
}

const isRawQueryRowObject = (value: unknown): value is RawQueryRowObject => {
  return typeof value === 'object' && value !== null
}

const readRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): unknown => {
  if (Array.isArray(row)) {
    return row[index]
  }
  if (isRawQueryRowObject(row)) {
    if (key in row) {
      return row[key]
    }
    const indexKey = index.toString()
    if (indexKey in row) {
      return row[indexKey]
    }
  }
  return undefined
}

/**
 * raw row から必須 string フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns 読み取った string 値。
 */
const readRequiredStringRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): string => {
  const value = readRawQueryField({ row, key, index })
  if (typeof value !== 'string') {
    throw new Error(`invalid action query raw row: ${key} must be string`)
  }
  return value
}

/**
 * raw row から nullable string フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns 読み取った string 値または null。
 */
const readNullableStringRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): string | null => {
  const value = readRawQueryField({ row, key, index })
  if (value === null) {
    return null
  }
  if (typeof value !== 'string') {
    throw new Error(
      `invalid action query raw row: ${key} must be string or null`,
    )
  }
  return value
}

/**
 * raw row から必須 number フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns 読み取った number 値。
 */
const readRequiredNumberRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): number => {
  const value = readRawQueryField({ row, key, index })
  if (typeof value !== 'number') {
    throw new Error(`invalid action query raw row: ${key} must be number`)
  }
  return value
}

/**
 * raw row から nullable number フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns 読み取った number 値または null。
 */
const readNullableNumberRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): number | null => {
  const value = readRawQueryField({ row, key, index })
  if (value === null) {
    return null
  }
  if (typeof value !== 'number') {
    throw new Error(
      `invalid action query raw row: ${key} must be number or null`,
    )
  }
  return value
}

/**
 * raw row から Action type フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns core で定義された Action type。
 */
const readActionTypeRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): ActionType => {
  const result = actionTypeUnionSchema.safeParse(
    readRawQueryField({ row, key, index }),
  )
  if (!result.success) {
    throw new Error(`invalid action query raw row: ${key} must be ActionType`)
  }
  return result.data
}

/**
 * raw row から nullable Action cross type フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns core で定義された Action cross type または null。
 */
const readNullableActionCrossTypeRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): ActionCrossType | null => {
  const value = readRawQueryField({ row, key, index })
  if (value === null) {
    return null
  }
  const result = actionCrossTypeUnionSchema.safeParse(value)
  if (!result.success) {
    throw new Error(
      `invalid action query raw row: ${key} must be ActionCrossType or null`,
    )
  }
  return result.data
}

/**
 * raw row から nullable Transfer direction フィールドを読み取る。
 * @param params raw row、列名、配列 row 用の列番号。
 * @returns core で定義された Transfer direction または null。
 */
const readNullableTransferDirectionRawQueryField = ({
  row,
  key,
  index,
}: {
  readonly row: unknown
  readonly key: string
  readonly index: number
}): TransferDirection | null => {
  const value = readRawQueryField({ row, key, index })
  if (value === null) {
    return null
  }
  const result = transferDirectionUnionSchema.safeParse(value)
  if (!result.success) {
    throw new Error(
      `invalid action query raw row: ${key} must be TransferDirection or null`,
    )
  }
  return result.data
}

/**
 * Action と Transfer を結合した raw SQL row を名前付き object に正規化する。
 * @param params database.all が返した raw row。
 * @returns parseToAction に渡す前段の Action query row。
 */
const normalizeRawActionQueryRow = ({
  row,
}: {
  readonly row: unknown
}): ActionQueryRow => ({
  actionId: readRequiredStringRawQueryField({
    row,
    key: 'actionId',
    index: 0,
  }),
  actionSourceId: readRequiredStringRawQueryField({
    row,
    key: 'actionSourceId',
    index: 1,
  }),
  actionOrder: readRequiredNumberRawQueryField({
    row,
    key: 'actionOrder',
    index: 2,
  }),
  actionType: readActionTypeRawQueryField({
    row,
    key: 'actionType',
    index: 3,
  }),
  actionSubType: readNullableNumberRawQueryField({
    row,
    key: 'actionSubType',
    index: 4,
  }),
  actionTimestamp: readRequiredNumberRawQueryField({
    row,
    key: 'actionTimestamp',
    index: 5,
  }),
  actionLoanId: readNullableStringRawQueryField({
    row,
    key: 'actionLoanId',
    index: 6,
  }),
  actionCrossId: readNullableStringRawQueryField({
    row,
    key: 'actionCrossId',
    index: 7,
  }),
  actionCrossType: readNullableActionCrossTypeRawQueryField({
    row,
    key: 'actionCrossType',
    index: 8,
  }),
  actionTargetId: readNullableStringRawQueryField({
    row,
    key: 'actionTargetId',
    index: 9,
  }),
  transferOrder: readNullableNumberRawQueryField({
    row,
    key: 'transferOrder',
    index: 10,
  }),
  transferDirection: readNullableTransferDirectionRawQueryField({
    row,
    key: 'transferDirection',
    index: 11,
  }),
  transferCryptoId: readNullableStringRawQueryField({
    row,
    key: 'transferCryptoId',
    index: 12,
  }),
  transferFiatId: readNullableStringRawQueryField({
    row,
    key: 'transferFiatId',
    index: 13,
  }),
  transferNftId: readNullableStringRawQueryField({
    row,
    key: 'transferNftId',
    index: 14,
  }),
  transferAmount: readNullableStringRawQueryField({
    row,
    key: 'transferAmount',
    index: 15,
  }),
  transferPrice: readNullableStringRawQueryField({
    row,
    key: 'transferPrice',
    index: 16,
  }),
})

const normalizeRawCryptoCurrencyRow = ({
  row,
}: {
  readonly row: unknown
}): RawCryptoCurrencyRow => {
  const id = readRawQueryField({ row, key: 'id', index: 0 })
  const name = readRawQueryField({ row, key: 'name', index: 1 })
  const symbol = readRawQueryField({ row, key: 'symbol', index: 2 })
  const icon = readRawQueryField({ row, key: 'icon', index: 3 })
  const coingeckoId = readRawQueryField({ row, key: 'coingeckoId', index: 4 })
  const marketCapUsd = readRawQueryField({ row, key: 'marketCapUsd', index: 5 })
  const priceDatasourceId = readRawQueryField({
    row,
    key: 'priceDatasourceId',
    index: 6,
  })
  const updatedAt = readRawQueryField({ row, key: 'updatedAt', index: 7 })

  if (
    typeof id !== 'string' ||
    typeof name !== 'string' ||
    typeof symbol !== 'string' ||
    typeof updatedAt !== 'number'
  ) {
    throw new Error('invalid crypto currency raw row')
  }

  return {
    id,
    name,
    symbol,
    icon: typeof icon === 'string' ? icon : null,
    coingeckoId: typeof coingeckoId === 'string' ? coingeckoId : null,
    marketCapUsd: typeof marketCapUsd === 'number' ? marketCapUsd : null,
    priceDatasourceId:
      typeof priceDatasourceId === 'string' ? priceDatasourceId : null,
    updatedAt,
  }
}

const normalizeRawNftRow = ({ row }: { readonly row: unknown }): RawNftRow => {
  const id = readRawQueryField({ row, key: 'id', index: 0 })
  const name = readRawQueryField({ row, key: 'name', index: 1 })
  const image = readRawQueryField({ row, key: 'image', index: 2 })
  const metadata = readRawQueryField({ row, key: 'metadata', index: 3 })
  const updatedAt = readRawQueryField({ row, key: 'updatedAt', index: 4 })

  if (typeof id !== 'string' || typeof updatedAt !== 'number') {
    throw new Error('invalid nft raw row')
  }

  return {
    id,
    name: typeof name === 'string' ? name : null,
    image: typeof image === 'string' ? image : null,
    metadata: typeof metadata === 'string' ? metadata : null,
    updatedAt,
  }
}

/**
 * ID 一覧を SQLite bind limit 内に分割して table rows を取得する。
 * @param params 対象 ID、chunk ごとの取得関数を受け取る。
 * @returns chunk ごとの取得結果を平坦化して返す。
 */
const selectRowsByChunkedIds = async <TRow>(params: {
  readonly ids: readonly string[]
  readonly selectChunk: (ids: readonly string[]) => Promise<TRow[]>
}): Promise<TRow[]> => {
  const { ids, selectChunk } = params
  const rows: TRow[] = []

  for (const chunk of divideArrayIntoChunks(ids, sqliteMaxHostParameterCount)) {
    rows.push(...(await selectChunk(chunk)))
  }

  return rows
}

const selectCryptoCurrenciesByIds = async ({
  database,
  ids,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly ids: readonly string[]
}): Promise<readonly CryptoCurrencyTableRow[]> =>
  await selectRowsByChunkedIds({
    ids,
    selectChunk: async (chunk) =>
      (
        await database.all<unknown>(sql`SELECT
  ${cryptoCurrencyTable.id} AS id,
  ${cryptoCurrencyTable.name} AS name,
  ${cryptoCurrencyTable.symbol} AS symbol,
  ${cryptoCurrencyTable.icon} AS icon,
  ${cryptoCurrencyTable.coingeckoId} AS coingeckoId,
  ${cryptoCurrencyTable.marketCapUsd} AS marketCapUsd,
  ${cryptoCurrencyTable.priceDatasourceId} AS priceDatasourceId,
  ${cryptoCurrencyTable.updatedAt} AS updatedAt
FROM ${cryptoCurrencyTable}
WHERE ${inArray(cryptoCurrencyTable.id, [...chunk])}`)
      ).map((row) => normalizeRawCryptoCurrencyRow({ row })),
  })

type CryptoCurrencyTableRow = typeof cryptoCurrencyTable.$inferSelect

const selectNftsByIds = async ({
  database,
  ids,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly ids: readonly string[]
}): Promise<readonly NftTableRow[]> =>
  await selectRowsByChunkedIds({
    ids,
    selectChunk: async (chunk) =>
      (
        await database.all<unknown>(sql`SELECT
  ${nftTable.id} AS id,
  ${nftTable.name} AS name,
  ${nftTable.image} AS image,
  ${nftTable.metadata} AS metadata,
  ${nftTable.updatedAt} AS updatedAt
FROM ${nftTable}
WHERE ${inArray(nftTable.id, [...chunk])}`)
      ).map((row) => normalizeRawNftRow({ row })),
  })

type NftTableRow = typeof nftTable.$inferSelect

/**
 * 指定期間の会計取引生成処理用 Action data を取得する
 * - 会計取引生成に不使用なデータは null や dummy で代用して DB からの転送データ量を削減する
 */
export const queryActionsForGenerateAccountingTransactionByRange =
  async (params: {
    database: DatabaseWithTransaction
    fiats: readonly FiatCurrency[]
    from: number
    to: number
    limit: number
    /** 前回取得した末尾アクション。指定時は並び順でその直後から取得する。 */
    after?: Pick<Action, 'timestamp' | 'crossType' | 'source' | 'order'>
  }): Promise<Action[]> => {
    const {
      database: { database },
      fiats,
      from,
      to,
      limit,
      after,
    } = params
    if (limit <= 0) return []

    const crossTypePriority = sql`CASE ${actionTable.crossType} WHEN 'middle' THEN 0 WHEN 'start' THEN 1 WHEN 'end' THEN 2 ELSE 3 END`
    const afterPriority =
      after?.crossType === 'middle'
        ? 0
        : after?.crossType === 'start'
          ? 1
          : after?.crossType === 'end'
            ? 2
            : 3
    const cursorCondition =
      after === undefined
        ? sql`1 = 1`
        : sql`(${actionTable.timestamp}, ${crossTypePriority}, ${actionTable.id}) > (${after.timestamp}, ${afterPriority}, ${`${after.source}_${after.order}`})`

    // action + action transfer 取得 (timestamp -> crossType -> id 順)
    // crossType は middle > start > end > その他(null 含む) の優先順位
    const rows = (
      await database.all<unknown>(sql`SELECT
  ${actionTable.id} AS actionId,
  ${actionTable.sourceId} AS actionSourceId,
  ${actionTable.order} AS actionOrder,
  ${actionTable.type} AS actionType,
  ${actionTable.subType} AS actionSubType,
  ${actionTable.timestamp} AS actionTimestamp,
  ${actionTable.loanId} AS actionLoanId,
  ${actionTable.crossId} AS actionCrossId,
  ${actionTable.crossType} AS actionCrossType,
  ${actionTable.targetId} AS actionTargetId,
  ${actionTransferTable.order} AS transferOrder,
  ${actionTransferTable.direction} AS transferDirection,
  ${actionTransferTable.cryptoId} AS transferCryptoId,
  ${actionTransferTable.fiatId} AS transferFiatId,
  ${actionTransferTable.nftId} AS transferNftId,
  ${actionTransferTable.amount} AS transferAmount,
  ${actionTransferTable.price} AS transferPrice
FROM (
  SELECT *
  FROM ${actionTable}
  WHERE
    ${actionTable.timestamp} >= ${from}
    AND ${actionTable.timestamp} <= ${to}
    AND ${cursorCondition}
  ORDER BY
    ${actionTable.timestamp} ASC,
    ${crossTypePriority},
    ${actionTable.id} ASC
  LIMIT ${limit}
) AS ${actionTable}
LEFT JOIN ${actionTransferTable}
ON ${actionTable.id} = ${actionTransferTable.actionId}
ORDER BY ${actionTable.timestamp}, ${crossTypePriority}, ${actionTable.id}, ${actionTransferTable.order};`)
    ).map((row) => normalizeRawActionQueryRow({ row }))

    const actionRows: (typeof actionTable.$inferSelect)[] = []
    const actionIds = new Set<string>()
    const transferRows: (typeof actionTransferTable.$inferSelect)[] = []
    for (const row of rows) {
      const id = row.actionId
      if (!actionIds.has(id)) {
        actionIds.add(id)
        actionRows.push({
          id,
          sourceId: row.actionSourceId,
          order: row.actionOrder,
          type: row.actionType,
          subType: row.actionSubType,
          timestamp: new Date(row.actionTimestamp),
          loanId: row.actionLoanId,
          crossId: row.actionCrossId,
          crossType: row.actionCrossType,
          targetId: row.actionTargetId,
          evidence: 'none',
          comment: null,
          app: null,
          totalPl: null,
          totalPlSortKey: null,
          locked: false,
          generatedTx: false,
          relatedAssetIds: null,
          fromAddresses: null,
          toAddresses: null,
          hasUnknownPriceTx: null,
          hasInsufficientAmountTx: null,
          apiId: null,
          csvId: null,
        })
      }
      if (
        row.transferOrder !== null &&
        row.transferDirection !== null &&
        row.transferAmount !== null
      ) {
        transferRows.push({
          actionId: id,
          order: row.transferOrder,
          direction: row.transferDirection,
          from: null,
          to: null,
          cryptoId: row.transferCryptoId,
          fiatId: row.transferFiatId,
          nftId: row.transferNftId,
          amount: row.transferAmount,
          price: row.transferPrice,
        })
      }
    }

    // 2. nft 取得
    const nftId = new Set<string>()
    for (const row of actionRows) {
      if (row.targetId) {
        nftId.add(row.targetId)
      }
    }
    for (const row of transferRows) {
      if (row.nftId) {
        nftId.add(row.nftId)
      }
    }
    const nftRows = await selectNftsByIds({
      database,
      ids: Array.from(nftId),
    })

    // 3. crypto 取得
    const cryptoId = new Set<string>()
    for (const row of transferRows) {
      if (row.cryptoId) {
        cryptoId.add(row.cryptoId)
      }
    }
    const cryptoRows = await selectCryptoCurrenciesByIds({
      database,
      ids: Array.from(cryptoId),
    })

    // 4. グルーピング
    const nftMap = new Map<string, typeof nftTable.$inferSelect>()
    for (const row of nftRows) {
      nftMap.set(row.id, row)
    }
    const cryptoMap = new Map<string, typeof cryptoCurrencyTable.$inferSelect>()
    for (const row of cryptoRows) {
      cryptoMap.set(row.id, row)
    }
    const transferMap = new Map<string, ActionTransferData[]>()
    for (const t of transferRows) {
      const list = transferMap.get(t.actionId) ?? []
      list.push({
        ...t,
        cryptoCurrencyTable: t.cryptoId
          ? (cryptoMap.get(t.cryptoId) ?? null)
          : null,
        nftTable: t.nftId ? (nftMap.get(t.nftId) ?? null) : null,
      })
      transferMap.set(t.actionId, list)
    }
    return actionRows.map((row) =>
      parseToAction(
        {
          ...row,
          // dummy action data
          evidence: 'none',
          comment: null,
          app: null,
          locked: false,
          generatedTx: false,
          relatedAssetIds: null,
          fromAddresses: null,
          toAddresses: null,
          hasUnknownPriceTx: null,
          hasInsufficientAmountTx: null,
          apiId: null,
          csvId: null,
          nftTable: row.targetId ? (nftMap.get(row.targetId) ?? null) : null,
          actionTransferTable: (transferMap.get(row.id) ?? []).sort(
            (a, b) => a.order - b.order,
          ),
        },
        fiats,
      ),
    )
  }
